/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { isEqual } from 'lodash';
import { ApiMultipleResponse } from 'models/ApiModels';
import { CreateUserPayload, EditUserPayload, RoleModel, UserParams, Users } from 'models/Users';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_COOKIE_MONSTER_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_COOKIE_MONSTER;

// Custom base query that handles Auth0 user IDs properly
const cookieMonsterBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const token = (api.getState() as ReduxState).app.accessToken;

  // Handle URL encoding for user IDs with special characters
  let url = typeof args === 'string' ? args : args.url;

  // If the URL contains a user ID pattern (auth0|...), ensure it's not double-encoded
  if (url.includes('/users/') && url.includes('auth0')) {
    // Extract the user ID part and ensure it's properly formatted
    const urlParts = url.split('/users/');

    if (urlParts.length > 1) {
      const userId = urlParts[1];

      // If the user ID is already encoded, decode it first
      const decodedUserId = decodeURIComponent(userId);

      // Reconstruct the URL with the properly formatted user ID
      url = `${urlParts[0]}/users/${decodedUserId}`;
    }
  }

  const adjustedArgs = typeof args === 'string' ? url : { ...args, url };

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState, endpoint }) => {
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
      }

      if (endpoint !== 'updateUserProfileImage') headers.set('Content-Type', 'application/json');

      return headers;
    }
  })(adjustedArgs, api, extraOptions);
};

export const cookieMonsterApi = createApi({
  reducerPath: 'cookieMonsterApi',
  baseQuery: cookieMonsterBaseQuery,
  tagTypes: ['User', 'Users', 'Roles'],
  endpoints: (builder) => ({
    getUser: builder.query<Users, string>({
      query: (userId) => ({
        url: `/users/${userId}`
      }),
      providesTags: ['User']
    }),
    getUsers: builder.query<ApiMultipleResponse<Users>, UserParams>({
      query: (params) => ({
        url: `/users/`,
        params
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().cookieMonsterApi;

          const lastQueryArgs = originalArgs ? { ...originalArgs, pageNumber: originalArgs?.pageNumber - 1 } : null;
          const lastQueryData = Object.values(queries).find((value) => isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft: any) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          //
        }
      },
      providesTags: ['Users']
    }),
    fetchUsersByNameOrEmail: builder.query<ApiMultipleResponse<Users>, { searchText: string; divisionId: string; pageNumber: number; take: number }>({
      query: ({ searchText, divisionId, pageNumber, take }) => ({
        url: '/users/',
        params: {
          pageNumber,
          take,
          divisionIds: divisionId,
          nameContains: searchText,
          emailContains: searchText
        }
      }),
      providesTags: ['Users']
    }),
    getRoles: builder.query<ApiMultipleResponse<RoleModel>, { includePermissions: boolean }>({
      query: (params) => ({
        url: `/roles/`,
        params
      }),
      providesTags: ['Roles']
    }),
    getDivisionUsers: builder.query<ApiMultipleResponse<Users>, { divisionId: string }>({
      query: ({ divisionId }) => {
        // Load all users at once with no pagination
        const params = {
          divisionIds: divisionId
        };

        return {
          url: `/users`,
          params
        };
      },
      transformResponse: (response: ApiMultipleResponse<Users>, meta, arg) => {
        // Filter users who have this division in their app_metadata
        const filteredData = response.data.filter((user) => user.app_metadata?.businesses?.some((business) => business.divisions?.some((division) => division.id === arg.divisionId)));

        return {
          ...response,
          data: filteredData
        };
      },
      providesTags: ['Users']
    }),
    updateUser: builder.mutation<Users, { userId: string; payload: EditUserPayload }>({
      query: ({ userId, payload }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body: payload
      }),
      invalidatesTags: ['User', 'Users']
    }),
    updateUserProfileImage: builder.mutation<Users, { userId: string; payload: FormData }>({
      query: ({ userId, payload }) => ({
        url: `/users/${userId}/profileImage`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['User', 'Users']
    }),
    createUser: builder.mutation<Users, { payload: CreateUserPayload }>({
      query: ({ payload }) => ({
        url: `/users/`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['User', 'Users']
    }),
    deleteUser: builder.mutation<Users, { userId: string; params: { wouldYouLikeToPlayAGame: boolean } }>({
      query: ({ userId, params }) => ({
        url: `/users/${userId}`,
        method: 'DELETE',
        params
      }),
      invalidatesTags: ['Users']
    }),
    removeUserFromDivision: builder.mutation<Users, { userId: string; divisionIdsToUnassign: string[] }>({
      query: ({ userId, divisionIdsToUnassign }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body: {
          divisionIdsToUnassign
        }
      }),
      invalidatesTags: ['Users']
    }),
    recoverUser: builder.mutation<Users, string>({
      query: (userId) => ({
        url: `/users/${userId}/recover`
      }),
      invalidatesTags: ['Users']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useDeleteUserMutation,
  useRecoverUserMutation,
  useGetUserQuery,
  useGetUsersQuery,
  useGetDivisionUsersQuery,
  useUpdateUserMutation,
  useCreateUserMutation,
  useGetRolesQuery,
  useUpdateUserProfileImageMutation,
  useRemoveUserFromDivisionMutation,
  useGetApiNameQuery,
  useGetVersionQuery
} = cookieMonsterApi;
